import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { LoadingIcon } from '../private/icons/LoadingIcon';
import { ButtonBase } from '../private/components/button/ButtonBase';
import { ArrowUpXS } from '../private/icons/ArrowUpXS';
import { ArrowForwardXS } from '../private/icons/ArrowForwardXS';
import { ArrowDownXS } from '../private/icons/ArrowDownXS';
import { ArrowBackXS } from '../private/icons/ArrowBackXS';
import useForkRef from '../private/hooks/useForkRef';
import useControlled from '../private/hooks/useControlled';

function createComponent(defaultComponent, displayName) {

  const Comp = (props, ref) => {

    const {
      as,
      ...other
    } = props;

    const Component = as || defaultComponent;

    return (
      <Component ref={ref} {...other} />
    );
  };

  Comp.displayName = displayName;
  return React.forwardRef(Comp);
}

const PillRoot = createComponent(ButtonBase, 'PillRoot');
const PillLabel = createComponent('span', 'PillLabel');

function isDeleteKeyboardEvent(keyboardEvent) {
  return keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete';
}

const getArrowIcon = (icon) => {
  switch (icon) {
    case 'up':
      return <ArrowUpXS className="sui-w-3 sui-h-2 sui-flex sui-items-center sui-justify-center" viewBox="0 0 12 8" />;
    case 'forward':
      return <ArrowForwardXS className="sui-w-2 sui-h-3 sui-flex sui-items-center sui-justify-center" viewBox="0 0 8 12" />;
    case 'down':
      return <ArrowDownXS className="sui-w-3 sui-h-2 sui-flex sui-items-center sui-justify-center" viewBox="0 0 12 8" />;
    case 'back':
      return <ArrowBackXS className="sui-w-2 sui-h-3 sui-flex sui-items-center sui-justify-center" viewBox="0 0 8 12" />;
    default:
      return null;
  }
};

/**
 * A versatile pill component is an interactive element that helps users filter content and make selections.
 *
 * The Pill component supports various states including selected, disabled, and loading.
 * It can function as a simple display element or as an interactive button with selection capabilities.
 * Pills can be customized with icons, arrows, and different visual variants.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} [props.arrow] - Configuration for arrow icons
 * @param {('up'|'forward'|'down'|'back')} [props.arrow.direction] - Direction of the arrow icon
 * @param {('leadingSlot'|'trailingSlot')} [props.arrow.position] - Position of the arrow icon
 * @param {React.ElementType} [props.component] - The component used for the root node
 * @param {boolean} [props.disabled=false] - If true, the component is disabled
 * @param {React.Node} props.label - The content of the pill
 * @param {React.Node} [props.leadingSlot] - Custom element before the pill label
 * @param {boolean} [props.loading=false] - If true, shows loading indicator
 * @param {Function} [props.onClick] - Click handler function
 * @param {Function} [props.onKeyDown] - KeyDown handler function
 * @param {Function} [props.onKeyUp] - KeyUp handler function
 * @param {boolean} [props.selectable=false] - If true, pill can be selected
 * @param {('small'|'regular'|'large')} [props.size='regular'] - Size of the pill
 * @param {React.Node} [props.trailingSlot] - Custom element after the pill label
 * @param {('outline'|'filled')} [props.variant='outline'] - Visual style variant
 *
 * @returns {React.Element} The rendered Pill component
 *
 * @example
 * ``` jsx
 *  import { Pill } from '@one-thd/sui-atomic-components';
 *
 * <Pill
 *   label="Filter Option"
 *   selectable
 *   onClick={handleClick}
 *   variant="filled"
 * />
 * ```
 */
const Pill = React.forwardRef((props, ref) => {

  const {
    arrow = {},
    component: ComponentProp,
    disabled = false,
    icon: iconProp,
    leadingSlot: leadingSlotProp,
    label,
    loading = false,
    onClick,
    onKeyDown,
    onKeyUp,
    size = 'regular',
    selectable = false,
    selected: selectedProp,
    trailingSlot: trailingSlotProp,
    variant = 'outline',
    ...other
  } = props;

  const pillRef = React.useRef(null);
  const handleRef = useForkRef(pillRef, ref);

  const [selected, setSelected] = useControlled({
    controlled: selectedProp,
    defaultValue: false
  });

  const reconciliateSelectedState = React.useCallback(() => {
    const isDefaultState = selectedProp === false && selectable === true;
    if (selectedProp !== selected && isDefaultState) {
      setSelected(selectedProp);
    }
  }, [selected, selectedProp, setSelected, selectable]);

  const handleClick = (event) => {
    if (selectable) {
      setSelected(!selected);
    }

    if (onClick) {
      onClick(event);
    }
  };

  const handleKeyDown = (event) => {
    // Ignore events from children of `Pill`.
    if (event.currentTarget === event.target && isDeleteKeyboardEvent(event)) {
      // Will be handled in keyUp, otherwise some browsers
      // might init navigation
      event.preventDefault();
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const handleKeyUp = (event) => {
    // Ignore events from children of `Pill`.
    if (event.currentTarget === event.target) {
      if (event.key === 'Escape' && pillRef.current) {
        pillRef.current.blur();
      }
    }

    if (onKeyUp) {
      onKeyUp(event);
    }
  };

  React.useEffect(() => reconciliateSelectedState(), [reconciliateSelectedState]);

  const component = onClick ? ButtonBase : ComponentProp || 'div';
  const clickable = component === ButtonBase || component === 'button' || component === 'a';

  const outlineClasses = classNames('sui-border-solid sui-border-1 sui-text-primary sui-focus-outline sui-fill-primary', {
    'sui-bg-strong/70 sui-border-input-active sui-border-2 [&:not(:active)]:hover:sui-bg-brand-subtle [&:not(:active)]:hover:sui-border-accent': selected,
    'sui-bg-primary/60 sui-border-primary [&:not(:active)]:hover:sui-bg-primary/30 [&:not(:active)]:hover:sui-border-accent': !selected,
    'sui-opacity-25': disabled
  });

  const filledClasses = classNames('sui-focus-outline active:sui-transform active:sui-ease-out active:sui-duration-150', {
    'sui-bg-inverse sui-text-inverse sui-fill-inverse [&:not(:active)]:hover:sui-bg-brand/50 [&:not(:active)]:hover:sui-text-primary active:sui-bg-inverse/99': selected,
    'sui-bg-medium/20 sui-text-primary sui-fill-primary [&:not(:active)]:hover:sui-bg-brand-subtle': !selected,
    'sui-opacity-25': disabled
  });

  const overlayClasses = classNames(
    'sui-flex sui-items-center sui-justify-center sui-gap-1 sui-align-center sui-rounded-full sui-w-full sui-h-full',
    'group-active:sui-transform group-active:sui-ease-out group-active:sui-duration-150', {
      'group-active:sui-backdrop-brightness-darken': variant === 'outline' || (variant === 'filled' && !selected),
      'group-active:sui-backdrop-brightness-lighten': variant === 'filled' && selected,
      'sui-px-2': size === 'small' || size === 'regular',
      'sui-px-4': size === 'large',
    });

  const classes = {
    root: classNames('sui-font-regular sui-text-sm sui-leading-none sui-whitespace-nowrap sui-flex sui-rounded-full sui-group', {
      'sui-cursor-default sui-pointer-events-none': !clickable || loading,
      'sui-text-transparent sui-relative': loading,
      'sui-h-6': size === 'small',
      'sui-h-8': size === 'regular',
      'sui-h-11': size === 'large',
      [outlineClasses]: variant === 'outline',
      [filledClasses]: variant === 'filled',
    }),
    label: 'sui-overflow-hidden sui-text-ellipsis',
    loading: 'sui-absolute sui-flex sui-items-center sui-justify-center sui-fill-inherit'
  };

  const moreProps = component === ButtonBase
    ? {
      component: ComponentProp || 'div'
    } : {};

  const icon = React.isValidElement(iconProp) && React.cloneElement(iconProp, { size: 'small' });
  const leadingIcon = React.isValidElement(leadingSlotProp) && React.cloneElement(leadingSlotProp, { size: 'small' });
  const trailingIcon = React.isValidElement(trailingSlotProp) && React.cloneElement(trailingSlotProp, { size: 'small' });
  const arrowIcon = getArrowIcon(arrow?.direction);
  const leadingSlot = (arrow?.direction && arrow?.position === 'leadingSlot') ? arrowIcon : leadingIcon;
  const trailingSlot = (arrow?.direction && arrow?.position === 'trailingSlot') ? arrowIcon : trailingIcon;

  return (
    <PillRoot
      as={component}
      className={classes.root}
      disabled={disabled ? true : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      ref={handleRef}
      tabIndex={disabled ? -1 : 0}
      {...moreProps}
      {...other}
    >
      <div className={overlayClasses}>
        { leadingSlot || icon }
        { loading && (
          <span className={classes.loading}>
            <LoadingIcon
              color="inherit"
              size={size === 'large' ? 'regular' : 'small'}
            />
          </span>
        )}
        <PillLabel className={classNames(classes.label)}>
          {label}
        </PillLabel>
        {trailingSlot}
      </div>
    </PillRoot>
  );
});

Pill.displayName = 'Pill';

Pill.propTypes = {
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  arrow: PropTypes.shape({
    direction: PropTypes.oneOf(['up', 'forward', 'down', 'back']),
    position: PropTypes.oneOf(['leadingSlot', 'trailingSlot'])
  }),
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component: PropTypes.elementType,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * Icon element.
   * @ignore
   */
  icon: PropTypes.element,
  /**
   * The content of the pill.
   */
  label: PropTypes.node,
  /**
   * Slot for placing a custom element before the pill label.
   * Useful for adding icons or other visual elements to the start of the pill.
   */
  leadingSlot: PropTypes.node,
  /**
   * If true, the loading indicator is shown.
   * @default false
   */
  loading: PropTypes.bool,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * @ignore
   */
  onKeyUp: PropTypes.func,
  /**
   * If `true`, the pill can be selected and will display selection states.
   * This enables the pill to show visual feedback when selected.
   * If `false`, the pill cannot be selected.
   * @default false
   */
  selectable: PropTypes.bool,
  /**
   * If `true`, the pill is visually marked as selected.
   * This prop is controlled internally or externally and is typically used in conjunction with `selectable`.
   * When provided, the component's selection state is managed by the parent.
   * @default false
   * @ignore
   */
  selected: PropTypes.bool,
  /**
   * The size of the pill.
   * - `small`: Compact pill, suitable for dense UIs.
   * - `regular`: Default size.
   * - `large`: Larger pill for prominent display.
   * @default 'regular'
   */
  size: PropTypes.oneOf(['small', 'regular', 'large']),
  /**
   * Slot for placing a custom element after the pill label.
   * Useful for adding icons or other visual elements to the end of the pill.
   */
  trailingSlot: PropTypes.node,
  /**
   * The visual style of the pill.
   * - `outline`: Displays the pill with a border and transparent background.
   * - `filled`: Displays the pill with a filled background.
   * @default 'outline'
   */
  variant: PropTypes.oneOf(['outline', 'filled'])
};

export { Pill };
